import { type ReactElement } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import Decimal from 'decimal.js'
import { object, string } from 'yup'
import { Box, InputAdornment, Stack } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { RelativePositionField } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { useResultsInvalidation } from '@editorHooks'
import { getModel } from '@queries'
import { updateRoofSlabBeam, updateSlabBeam } from '@mutations'

const relativePositionSchema = object({
  relative_position: string()
    .default('0')
    .test('is-relative', 'Balken liegt außerhalb der Deckenposition', value => {
      try {
        const decimalValue = new Decimal(value ?? '0')
        return decimalValue.gte(0) && decimalValue.lte('1')
      } catch {
        return false
      }
    }),
})

export type AbsolutePositionBlockProps = {
  beamGuid: string
  elementType: 'slab_beams' | 'roof_slab_beams'
  slabLength: number
  relativePositionDefault: string
  disableForm: boolean
}

const AbsolutePositionBlock = ({
  beamGuid,
  elementType,
  slabLength,
  relativePositionDefault,
  disableForm,
}: AbsolutePositionBlockProps): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const disableFormValidation = useAppStore(state => state.disableFormValidation)

  // Not a Form2: this block lives inside the detail pane's design Form2, and a
  // nested <form> is invalid HTML — so the form context is provided without a
  // form element and saving goes through onClick. Mirror Form2's emergency
  // validation bypass (the "Validierung von Formularen deaktivieren" toggle).
  const methods = useForm({
    defaultValues: { relative_position: relativePositionDefault },
    ...(disableFormValidation ? {} : { resolver: yupResolver(relativePositionSchema) }),
  })

  const { mutateAsync, isLoading } = useMutation(
    (relative_position: string) =>
      elementType === 'slab_beams'
        ? updateSlabBeam.request(projectId as string, beamGuid, relative_position)
        : updateRoofSlabBeam.request(projectId as string, beamGuid, relative_position),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        await invalidateResults(projectId as string)
        enqueueSnackbar('Deckenbalken erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Deckenbalkens'), {
          variant: 'error',
        })
      },
    },
  )

  const onSave = methods.handleSubmit(async values => {
    await mutateAsync(values.relative_position)
  })

  return (
    <FormProvider {...methods}>
      <Box p={1} border={1} borderColor="grey.200" borderRadius={1}>
        <Stack spacing={1}>
          <RelativePositionField
            label="Absolute Position"
            length={slabLength}
            size="small"
            name="relative_position"
            disabled={disableForm}
            InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
            data-cy="absolute-slab-beam-position-input"
          />
          <Stack direction="row" justifyContent="end">
            <SaveButton
              loading={isLoading}
              disabled={disableForm || !methods.formState.isDirty}
              size="small"
              onClick={() => void onSave()}
              data-cy="submit-slab-beam-position"
            >
              Speichern
            </SaveButton>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export default AbsolutePositionBlock
